"use client";

import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "convex/react";
import { formatDistanceToNow } from "date-fns";
import Image from "next/image";
import Link from "next/link";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ShareButton } from "@/components/ui/share-button";
import { VotingButtons } from "@/components/ui/voting-buttons";
import { useAnonymousVote } from "@/hooks/use-anonymous-vote";
import { formatNumber } from "@/lib/utils";

type Props = {
  build: {
    _id: Id<"weaponBuilds">;
    _creationTime: number;
    name: string;
    slug: string;
    user: {
      _id: Id<"users">;
      name: string;
      pictureUrl?: string;
      username?: string;
    };
    weapon: {
      _id: Id<"weapons">;
      name: string;
      category: string;
      logoUrl: string | null;
    };
    stats: {
      upvotes: number;
      downvotes: number;
      views: number;
      comments: number;
    };
  };
};

function getInitials(name: string) {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function WeaponBuildHeader({ build }: Props) {
  // Anonymous vote token management
  const { token, isInitialized, ensureToken } = useAnonymousVote();

  // Check if current user is the owner
  const currentUser = useQuery(api.users.queries.current);
  const isOwner = currentUser?._id === build.user._id;

  // Get user's vote
  const userVote = useQuery(api.weaponBuilds.queries.getUserVote, {
    weaponBuildId: build._id,
    anonymousToken: token ?? undefined,
  });

  // Voting mutation
  const voteWeaponBuild = useMutation(
    api.weaponBuilds.mutations.voteWeaponBuild
  );

  async function handleVote(type: "upvote" | "downvote") {
    try {
      // Authenticated users don't need a token
      if (currentUser) {
        await voteWeaponBuild({
          weaponBuildId: build._id,
          type,
        });
      } else {
        // For anonymous users, ensure we have a token
        const voteToken = await ensureToken();
        await voteWeaponBuild({
          weaponBuildId: build._id,
          type,
          anonymousToken: voteToken,
        });
      }
    } catch (error) {
      toast.error("Failed to submit vote", {
        description:
          error instanceof Error ? error.message : "Failed to submit vote",
      });
    }
  }

  return (
    <div className="rounded-[8px] border border-[#262A2D] bg-[#141618] p-6">
      <div className="flex flex-col gap-5">
        {/* Title and Weapon Info */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1 space-y-3">
            <h1 className="font-bold font-heading text-3xl text-[#EAEBEC] leading-tight tracking-wide sm:text-4xl">
              {build.name.toUpperCase()}
            </h1>

            <div className="flex flex-wrap items-center gap-2">
              {build.weapon.logoUrl ? (
                <Image
                  alt={build.weapon.name}
                  className="h-8 w-8 object-contain"
                  height={32}
                  src={build.weapon.logoUrl}
                  width={32}
                />
              ) : null}
              <span className="rounded-[4px] border border-[#33383C] bg-[#0C0D0E] px-2.5 py-1 font-mono text-[#EAEBEC] text-xs tracking-wide">
                {build.weapon.name}
              </span>
              <span className="rounded-[4px] bg-[#46C8E0] px-2.5 py-1 font-bold font-mono text-[#0a0b0c] text-xs">
                {build.weapon.category}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <ShareButton
              className="rounded-[5px] border border-[#33383C] bg-transparent font-mono text-[#A9AFB3] text-xs tracking-wider hover:border-[#F5A23E] hover:bg-transparent hover:text-[#EAEBEC]"
              description={`Check out this ${build.weapon.name} build by ${build.user.name}. Optimized for ${build.weapon.category} gameplay!`}
              size="sm"
              title={build.name}
              variant="outline"
            />
            {isOwner && (
              <Button
                asChild
                className="rounded-[5px] border border-[#33383C] bg-transparent font-mono text-[#A9AFB3] text-xs tracking-wider hover:border-[#F5A23E] hover:bg-transparent hover:text-[#EAEBEC]"
                size="sm"
                variant="outline"
              >
                <Link href={`/weapons/builds/${build.slug}/edit`}>EDIT</Link>
              </Button>
            )}
          </div>
        </div>

        {/* Creator and Stats */}
        <div className="flex flex-col gap-4 border-[#262A2D] border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          {/* Creator */}
          <div className="relative flex items-center gap-3 rounded-md px-3 py-1 hover:bg-[#0C0D0E]">
            <Avatar className="h-10 w-10">
              <AvatarImage alt={build.user.name} src={build.user.pictureUrl} />
              <AvatarFallback className="bg-[#2b6cb0] text-white">
                {getInitials(build.user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <Link
                className="font-medium text-[#EAEBEC] text-sm transition-colors hover:text-[#F5A23E]"
                href={`/profile/${build.user.username || build.user._id}`}
              >
                <span className="absolute inset-0" />
                {build.user.name}
              </Link>
              <span className="font-mono text-[#6a7075] text-xs">
                {formatDistanceToNow(build._creationTime, { addSuffix: true })}
              </span>
            </div>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap items-center gap-6 font-mono">
            <VotingButtons
              downvotes={build.stats.downvotes}
              isLoading={userVote === undefined || !isInitialized}
              onVote={handleVote}
              upvotes={build.stats.upvotes}
              userVote={userVote?.type ?? null}
            />
            <div className="text-center">
              <div className="font-bold text-[#EAEBEC] text-sm">
                {build.stats.comments}
              </div>
              <div className="text-[#6a7075] text-[10px] tracking-[0.1em]">
                COMMENTS
              </div>
            </div>
            <div className="text-center">
              <div className="font-bold text-[#EAEBEC] text-sm">
                {formatNumber(build.stats.views)}
              </div>
              <div className="text-[#6a7075] text-[10px] tracking-[0.1em]">
                VIEWS
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
